import {
  concat,
  EMPTY,
  forkJoin,
  Observable,
  of,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  concatMap,
  delay as observableDelay,
  filter,
  flatMap,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  compact,
  forOwn,
  identity,
  isEmpty,
  keys,
  pick,
  pickBy,
  reduce,
} from 'lodash';
import {
  ActionsEnum as AppActionsEnum,
  hideGlobalBanner,
  HideGlobalBanner,
  SetGlobalBanner,
  setGlobalBanner,
} from '../../store/app.actions';
import { EntityTypesEnum } from '../../app.enum';
import {
  setLocalStorageItem,
  SetLocalStorageItem,
} from '../browser-manager-data/browser-manager-data.actions';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import {
  BannerCanvasTypesEnum,
  UPDATE_FEATURE_FRAMEWORK_DATA_DELAY,
} from '../features-data/features-data.data';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import {
  ActionsEnum,
  AddToLimitedSelectedList,
  AddToSelectedList,
  addToSelectedList,
  batchDeleteEntityNetwork,
  BatchDeleteEntityNetwork,
  batchDeleteEntityNetworkError,
  batchDeleteEntityNetworkSuccess,
  batchDeleteEntityStore,
  BatchDeleteEntityStore,
  batchSaveEntityStore,
  BatchSaveEntityStore,
  clearList,
  ClearList,
  CreateNewEditingEntity,
  DeleteEntityNetwork,
  deleteEntityNetworkError,
  DeleteEntityNetworkError,
  deleteEntityNetworkSuccess,
  DeleteEntityNetworkSuccess,
  deleteEntityStore,
  DeleteEntityStore,
  DeleteSelectedEntities,
  FetchFilters,
  fetchFilters,
  FetchFiltersError,
  fetchFiltersError,
  FetchList,
  fetchListError,
  FetchListError,
  GetEntityById,
  PersistSearchParams,
  persistSearchParams,
  RefreshEditingEntity,
  RemoveFromSelectedList,
  ResetEditingEntity,
  resetEditingEntity,
  ResetSelectedList,
  resetSelectedList,
  saveEditingEntity,
  SaveEditingEntity,
  SaveEntityNetwork,
  saveEntityNetwork,
  saveEntityNetworkError,
  SaveEntityNetworkError,
  saveEntityNetworkSuccess,
  SaveEntityNetworkSuccess,
  SaveEntityStore,
  saveEntityStore,
  SaveNewEntity,
  SearchList,
  setAllLists,
  SetAllLists,
  setDispatchedResponse,
  SetDispatchedResponse,
  SetEditingEntity,
  setEditingEntity,
  SetEditingEntityContent,
  SetEditingEntityField,
  SetEditingEntityProperties,
  setFilters,
  SetFilters,
  setFiltersFetched,
  SetFiltersFetched,
  setListFetched,
  SetListFetched,
  setRefreshingFilters,
  SetRefreshingFilters,
  SetSearchParams,
  setSearchParams,
  setSelectedList,
  UpdateEntity,
  UpdateList,
  updateList,
} from './entity-data.actions';
import {
  IEntity,
  IEntityData,
  IEntityFilters,
  IFetchFilterKey,
  ISearchParams,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from './entity-data.data';
import { IEntityState, IFilteredLists } from './entity-data.reducer';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { EntityDataService } from './entity-data.service';
import { EntityDataStore } from './entity-data.store';
import { DialogService } from '../dialog/dialog.service';
import { EntityDataBuilder } from './entity-data.builder';
import { List, Set } from 'immutable';
import { Highlight } from '../search-data/sub-models/highlight.model';
import { NavigationService } from '../navigation/navigation.service';
import { ActionsEnum as SettingsActionsEnum } from '../settings-data/settings-data.actions';
import { GlobalBannerTypesEnum } from '../shared/components/global-banner/global-banner.enum';
import { AppStore } from '../../store';
import { IGlobalBanner } from '../shared/components/global-banner/global-banner.interface';
import { merger } from '../../utils/helpers/model.helper';

// should only be used as a superclass; this class is not injectable
export abstract class EntityDataEffects<
  T extends IEntityState,
  U extends IEntity
> {
  protected model: U;
  protected listFilterKeys: IFetchFilterKey;
  protected yearFilterKey: string;
  protected quotaName: string;
  protected entityType: EntityTypesEnum;
  protected baseSearchParams: ISearchParams = {
    page: 1,
    page_size: 500,
  };
  protected refreshFiltersDelay: number;

  constructor(
    protected actions: Actions,
    protected appStore: AppStore,
    protected entityDataService: EntityDataService,
    protected entityDataStore: EntityDataStore<T, U>,
    protected entityDataBuilder: EntityDataBuilder<U>,
    protected settingsDataStore: SettingsDataStore,
    protected featuresDataStore: FeaturesDataStore,
    protected translateService: TranslateService,
    protected globalBannerService: GlobalBannerService,
    protected dialogService: DialogService,
    protected navigationService: NavigationService,
    protected localStorageService: LocalStorageService,
    protected featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    this.model = entityDataBuilder.model;
    this.entityType = entityDataBuilder.entityType;
    this.quotaName = entityDataBuilder.quotaName;
    this.listFilterKeys = entityDataBuilder.listFilterKeys;
    this.yearFilterKey = entityDataBuilder.yearFilterKey;
    this.refreshFiltersDelay = entityDataBuilder.refreshFiltersDelay;
  }

  /**
   * Load sortBy and sortOrder params from local storage during app initialisation
   */
  protected loadSearchParams: Observable<
    SetSearchParams | PersistSearchParams
  > = this.actions.pipe(
    ofType(AppActionsEnum.INIT_APP),
    take(1),
    flatMap(() => {
      const effects = [];

      // for each entity list:
      // - load search params from local storage;
      // - OR override local storage with default values;
      forOwn(this.listFilterKeys, (headerKey, listFilter) => {
        try {
          const sortByHeader = JSON.parse(
            this.localStorageService.getItem(headerKey)
          );
          const searchParams = pick(sortByHeader, [
            SearchParamsEnum.sort_by,
            SearchParamsEnum.sort_order,
            SearchParamsEnum[this.yearFilterKey],
          ]);

          effects.push(
            this.entityDataBuilder.validateSearchParams(
              searchParams,
              listFilter
            )
              ? setSearchParams(this.entityType, searchParams, listFilter)
              : persistSearchParams(this.entityType, listFilter, headerKey)
          );
        } catch {
          effects.push(
            persistSearchParams(this.entityType, listFilter, headerKey)
          );
        }
      });

      return concat(effects);
    })
  );

  /**
   * Persist current sortBy and sortOrder params into local storage
   */
  protected persistSearchParams: Observable<SetLocalStorageItem> = this.actions.pipe(
    ofType(ActionsEnum.PERSIST_SEARCH_PARAMS),
    filter(
      ({ payload }: PersistSearchParams) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: PersistSearchParams) => {
      const { listFilter, persistKey } = payload;

      return persistKey
        ? this.entityDataStore.getSearchParams(listFilter).pipe(
            take(1),
            map((params: ISearchParams) =>
              setLocalStorageItem(persistKey, JSON.stringify(params))
            )
          )
        : EMPTY;
    })
  );

  /**
   * Fetch list based on url or current params in the store
   */
  protected fetchList: Observable<
    PersistSearchParams | UpdateList | FetchListError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_LIST),
    filter(({ payload }: FetchList) => payload.entityType === this.entityType),
    switchMap(({ payload }: FetchList) => {
      const { listFilter, searchParams: searchParamsFromPayload } = payload;

      return this.entityDataStore.getSearchParams(listFilter).pipe(
        take(1),
        flatMap((currentSearchParams: ISearchParams) => {
          const sortAndFilterParams = this.entityDataBuilder.buildSearchParams(
            currentSearchParams,
            searchParamsFromPayload,
            listFilter
          );

          const params = {
            ...this.baseSearchParams,
            ...sortAndFilterParams,
          };

          return this.entityDataService.searchEntities(params);
        }),
        withLatestFrom(
          this.settingsDataStore.settings$,
          this.featuresDataStore.featureToggles$
        ),
        flatMap(
          ([{ entities, searchParams }, settings, toggles]: [
            IEntityData,
            Settings,
            Toggles
          ]) => {
            return concat([
              this.buildUpdateList(
                this.entityType,
                entities[0],
                searchParams,
                listFilter,
                settings,
                toggles
              ),
              persistSearchParams(
                this.entityType,
                listFilter,
                this.listFilterKeys[listFilter]
              ),
            ]);
          }
        ),
        catchError((error) =>
          of(
            fetchListError(
              this.entityType,
              new Error('fetch list error'),
              error,
              listFilter
            )
          )
        )
      );
    })
  );

  /**
   * Search list based on query params
   */
  protected searchList: Observable<
    PersistSearchParams | UpdateList | FetchListError
  > = this.actions.pipe(
    ofType(ActionsEnum.SEARCH_LIST),
    filter(({ payload }: SearchList) => payload.entityType === this.entityType),
    switchMap(({ payload }: SearchList) => {
      const {
        listFilter,
        searchParams: searchParamsFromPayload,
        globalSearch,
        persistParams,
      } = payload;

      const validSearchParams = pickBy(searchParamsFromPayload, identity);
      const params = {
        ...this.baseSearchParams,
        ...validSearchParams,
      };

      return this.entityDataService.searchEntities(params, globalSearch).pipe(
        take(1),
        withLatestFrom(
          this.settingsDataStore.settings$,
          this.featuresDataStore.featureToggles$
        ),
        flatMap(
          ([{ entities, highlights, searchParams }, settings, toggles]: [
            IEntityData,
            Settings,
            Toggles
          ]) => {
            const effects: any[] = [
              this.buildUpdateList(
                this.entityType,
                entities[0],
                searchParams,
                listFilter,
                settings,
                toggles,
                highlights
              ),
            ];

            if (persistParams) {
              effects.push(
                persistSearchParams(
                  this.entityType,
                  listFilter,
                  this.listFilterKeys[listFilter]
                )
              );
            }

            return concat(effects);
          }
        ),
        catchError((error) =>
          of(
            fetchListError(
              this.entityType,
              new Error('search list error'),
              error,
              listFilter
            )
          )
        )
      );
    })
  );

  /**
   * Fetch next page of a list
   */
  protected fetchNextPage: Observable<
    UpdateList | SetListFetched | FetchListError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_NEXT_PAGE),
    filter(({ payload }: FetchList) => payload.entityType === this.entityType),
    switchMap(({ payload }: FetchList) => {
      const { listFilter } = payload;

      return this.entityDataStore.getSearchParams(listFilter).pipe(
        take(1),
        flatMap((currentSearchParams: ISearchParams) => {
          if (currentSearchParams.next_page_url) {
            const params: ISearchParams = this.entityDataBuilder.buildSearchParamsFromUrl(
              currentSearchParams.next_page_url
            );

            return this.entityDataService.searchEntities(params);
          } else {
            return of(void 0);
          }
        }),
        withLatestFrom(
          this.settingsDataStore.settings$,
          this.featuresDataStore.featureToggles$
        ),
        map(
          ([entityData, settings, toggles]: [
            IEntityData,
            Settings,
            Toggles
          ]) => {
            if (entityData) {
              const { entities, searchParams } = entityData;

              return this.buildUpdateList(
                this.entityType,
                entities[0],
                searchParams,
                listFilter,
                settings,
                toggles
              );
            } else {
              return setListFetched(this.entityType, true, listFilter);
            }
          }
        ),
        catchError((error) =>
          of(
            fetchListError(
              this.entityType,
              new Error('fetch next page error'),
              error,
              listFilter
            )
          )
        )
      );
    })
  );

  /**
   * Fetch list filters
   */
  protected fetchFilters: Observable<
    SetFilters | FetchFiltersError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_FILTERS),
    filter(
      ({ payload }: FetchFilters) =>
        payload.entityType === this.entityType && payload.delay === 0
    ),
    flatMap(({ payload }: FetchFilters) => {
      const { listFilter } = payload;

      return this.entityDataService.getFilters(listFilter).pipe(
        take(1),
        map((entityFilters: IEntityFilters) =>
          setFilters(this.entityType, entityFilters, listFilter)
        ),
        catchError((error) =>
          of(
            fetchFiltersError(
              this.entityType,
              new Error('fetch filters error'),
              error,
              listFilter
            )
          )
        )
      );
    })
  );

  /**
   * Trigger fetch list filter with delay
   */
  protected triggerRefreshFiltersWithDelay: Observable<FetchFilters> = this.actions.pipe(
    ofType(
      ActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
      ActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS
    ),
    filter(
      ({ payload }: SaveEntityNetworkSuccess | DeleteEntityNetworkSuccess) =>
        payload.entityType === this.entityType
    ),
    withLatestFrom(
      this.settingsDataStore.settings$,
      this.featuresDataStore.featureToggles$
    ),
    map(
      ([{ payload }, settings, toggles]: [
        SaveEntityNetworkSuccess | DeleteEntityNetworkSuccess,
        Settings,
        Toggles
      ]) => {
        const { entity } = payload;
        const listFilter = this.entityDataBuilder.buildListFilter(
          entity,
          settings,
          toggles
        );

        return fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          listFilter
        );
      }
    )
  );

  /**
   * Get entity from network
   */
  protected getEntityById: Observable<SetDispatchedResponse> = this.actions.pipe(
    ofType(ActionsEnum.GET_ENTITY_BY_ID),
    filter(
      ({ payload }: GetEntityById) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: GetEntityById) => {
      const { dispatchId, entityId } = payload;
      const action = ActionsEnum.GET_ENTITY_BY_ID;

      // TODO [refactor]: try to get entity from local store first (useLocal)
      return this.entityDataService.getEntity(entityId).pipe(
        take(1),
        flatMap((entity) =>
          // handling null case in effects until entityDataService.getEntity() and all stores have been refactored
          entity
            ? of(
                setDispatchedResponse(
                  this.entityType,
                  dispatchId,
                  action,
                  true,
                  entity
                )
              )
            : observableThrowError(
                new Error(
                  `attempted to get entity with bad id: ${
                    entityId || 'NOT-PRESENT'
                  }`
                )
              )
        ),
        catchError((error) => {
          return this.settingsDataStore.getSettings().pipe(
            map((settings: Settings) => {
              const entity = this.entityDataBuilder.buildEntity(
                {},
                settings,
                false,
                false
              );

              return setDispatchedResponse(
                this.entityType,
                dispatchId,
                action,
                false,
                entity,
                new Error('get entity by id error'),
                error
              );
            })
          );
        })
      );
    })
  );

  /**
   * Save new entity
   */
  protected saveNewEntity: Observable<
    SaveEntityStore | SaveEntityNetwork | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.SAVE_NEW_ENTITY),
    filter(
      ({ payload }: SaveNewEntity) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SaveNewEntity) => {
      const { dispatchId, entity } = payload;
      const action = ActionsEnum.SAVE_NEW_ENTITY;
      const withinQuotaLimits$ = this.quotaName
        ? this.featuresDataStore.canCreate(this.quotaName)
        : of(true);

      return withinQuotaLimits$.pipe(
        withLatestFrom(this.settingsDataStore.getSettings()),
        flatMap(([withinQuotaLimits, settings]: [boolean, Settings]) => {
          const effects = [];

          if (withinQuotaLimits) {
            const newEntity = this.entityDataBuilder.buildEntity(
              entity,
              settings,
              false,
              true
            );

            effects.push(
              saveEntityStore(this.entityType, newEntity, true),
              saveEntityNetwork(
                dispatchId,
                action,
                this.entityType,
                newEntity,
                true
              )
            );
          } else {
            const emptyEntity = this.entityDataBuilder.buildEntity(
              {},
              settings,
              false,
              false
            );

            this.dialogService.openGrowthDialog(
              BannerCanvasTypesEnum.quota_reached,
              this.quotaName
            );

            effects.push(
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                action,
                false,
                emptyEntity
              )
            );
          }

          return concat(effects);
        })
      );
    })
  );

  /**
   * On save new entity error, delete new entity from strore
   */
  protected saveNewEntityError: Observable<DeleteEntityStore> = this.actions.pipe(
    ofType(ActionsEnum.SAVE_ENTITY_NETWORK_ERROR),
    filter(
      ({ payload }: SaveEntityNetworkError) =>
        payload.entityType === this.entityType &&
        payload.sourceAction === ActionsEnum.SAVE_NEW_ENTITY
    ),
    switchMap(({ payload }: SaveEntityNetworkError) => {
      const { entity } = payload;

      return of(deleteEntityStore(this.entityType, entity, true));
    })
  );

  /**
   * Update existing entity content by id
   */
  protected updateEntity: Observable<
    SaveEntityStore | SaveEntityNetwork | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.UPDATE_ENTITY),
    filter(
      ({ payload }: UpdateEntity) => payload.entityType === this.entityType
    ),
    withLatestFrom(this.settingsDataStore.settings$),
    switchMap(([{ payload }, settings]: [UpdateEntity, Settings]) => {
      const { dispatchId, entityId, properties } = payload;

      return this.entityDataService.getEntity(entityId).pipe(
        take(1),
        flatMap((entity: U) => {
          // handling null case in effects until entityDataService.getEntity() and all stores have been refactored
          if (!entity) {
            return observableThrowError(
              new Error(
                `attempted to get entity with bad id: ${
                  entityId || 'NOT-PRESENT'
                }`
              )
            );
          }

          const newEntityProps = entity.mergeContent(properties);
          const newEntity = this.entityDataBuilder.buildEntity(
            newEntityProps,
            settings,
            true,
            false,
            true
          );

          return concat([
            saveEntityStore(this.entityType, newEntity, false),
            saveEntityNetwork(
              dispatchId,
              ActionsEnum.UPDATE_ENTITY,
              this.entityType,
              newEntity,
              false,
              entity
            ),
          ]);
        }),
        catchError((error) => {
          const emptyEntity = this.entityDataBuilder.buildEntity(
            {},
            settings,
            false,
            false
          );

          return of(
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              ActionsEnum.UPDATE_ENTITY,
              false,
              emptyEntity,
              new Error('get entity by id error'),
              error
            )
          );
        })
      );
    })
  );

  /**
   * Refresh the editing entity
   */
  protected refreshEditingEntity: Observable<
    SetEditingEntity | SaveEntityStore
  > = this.actions.pipe(
    ofType(ActionsEnum.REFRESH_EDITING_ENTITY),
    filter(
      ({ payload }: RefreshEditingEntity) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: RefreshEditingEntity) => {
      const { saveEntityToStore } = payload;

      return this.entityDataStore.getValidEditingEntity().pipe(
        take(1),
        flatMap((entity: U) => this.entityDataService.getEntity(entity.id)),
        filter((x) => !!x),
        flatMap((entity: U) => {
          const effects: any[] = [
            setEditingEntity(this.entityType, entity, true),
          ];

          if (saveEntityToStore) {
            effects.push(saveEntityStore(this.entityType, entity, false));
          }

          return concat(effects);
        })
      );
    })
  );

  /**
   * Update editing entity content
   */
  protected setEditingEntityContent: Observable<
    SetEditingEntity | SaveEditingEntity | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.SET_EDITING_ENTITY_CONTENT),
    filter(
      ({ payload }: SetEditingEntityContent) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SetEditingEntityContent) => {
      const { dispatchId, properties, saveEditing } = payload;

      return this.entityDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        withLatestFrom(this.settingsDataStore.settings$),
        flatMap(([entity, settings]: [U, Settings]) => {
          const newEntityProps = entity.mergeContent(properties);
          const newEntity = this.entityDataBuilder.buildEntity(
            newEntityProps,
            settings,
            true,
            newEntityProps.isNew(),
            true
          );
          const effects: any[] = [
            setEditingEntity(this.entityType, newEntity),
            saveEditing
              ? saveEditingEntity(dispatchId, this.entityType)
              : setDispatchedResponse(
                  this.entityType,
                  dispatchId,
                  ActionsEnum.SET_EDITING_ENTITY_CONTENT,
                  true,
                  newEntity
                ),
          ];

          return concat(effects);
        })
      );
    })
  );

  /**
   * Update all editing entity properties
   */
  protected setEditingEntityProperties: Observable<SetEditingEntity> = this.actions.pipe(
    ofType(ActionsEnum.SET_EDITING_ENTITY_PROPERTIES),
    filter(
      ({ payload }: SetEditingEntityProperties) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SetEditingEntityProperties) => {
      const { properties } = payload;

      return this.entityDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        withLatestFrom(this.settingsDataStore.settings$),
        map(([entity, settings]: [U, Settings]) => {
          const newEntityProps = entity.decorateWithKeys(
            merger(entity, properties)
          );
          const newEntity = this.entityDataBuilder.buildEntity(
            newEntityProps,
            settings,
            true,
            newEntityProps.isNew(),
            true
          );

          return setEditingEntity(this.entityType, newEntity);
        })
      );
    })
  );

  /**
   * Update single field with a value
   */
  protected setEditingEntityField: Observable<SetEditingEntity> = this.actions.pipe(
    ofType(ActionsEnum.SET_EDITING_ENTITY_FIELD),
    filter(
      ({ payload }: SetEditingEntityField) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SetEditingEntityField) => {
      const { key, value } = payload;

      return this.entityDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        withLatestFrom(this.settingsDataStore.settings$),
        map(([entity, settings]: [U, Settings]) => {
          const newEntityProps = entity.setFieldInContent(key, value);
          const newEntity = this.entityDataBuilder.buildEntity(
            newEntityProps,
            settings,
            true,
            newEntityProps.isNew(),
            true
          );

          return setEditingEntity(this.entityType, newEntity);
        })
      );
    })
  );

  /**
   * Create a new editing entity
   */
  protected createNewEditingEntity: Observable<
    SetEditingEntity | ResetEditingEntity
  > = this.actions.pipe(
    ofType(ActionsEnum.CREATE_NEW_EDITING_ENTITY),
    filter(
      ({ payload }: CreateNewEditingEntity) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: CreateNewEditingEntity) => {
      const { goBackUrl } = payload;
      const withinQuotaLimits$ = this.quotaName
        ? this.featuresDataStore.canCreate(this.quotaName)
        : of(true);

      return withinQuotaLimits$.pipe(
        withLatestFrom(this.settingsDataStore.settings$),
        map(([withinQuotaLimits, settings]: [boolean, Settings]) => {
          if (withinQuotaLimits) {
            return setEditingEntity(
              this.entityType,
              this.entityDataBuilder.buildEntity({}, settings, false, true)
            );
          } else {
            this.dialogService.openGrowthDialog(
              BannerCanvasTypesEnum.quota_reached,
              this.quotaName
            );

            if (goBackUrl) {
              this.navigationService.goBack(goBackUrl);
            }

            return resetEditingEntity(this.entityType);
          }
        })
      );
    })
  );

  /**
   * Save editing entity
   */
  protected saveEditingEntity: Observable<
    SaveEntityStore | SaveEntityNetwork
  > = this.actions.pipe(
    ofType(ActionsEnum.SAVE_EDITING_ENTITY),
    filter(
      ({ payload }: SaveEditingEntity) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SaveEditingEntity) => {
      const { dispatchId } = payload;

      return this.entityDataStore.getEditingEntity().pipe(
        take(1),
        withLatestFrom(
          this.entityDataStore.getOriginalEditingEntity(),
          this.settingsDataStore.settings$
        ),
        flatMap(([entity, originalEntity, settings]: [U, U, Settings]) => {
          const newEntity = this.entityDataBuilder.buildEntity(
            entity,
            settings,
            entity.isDirty(),
            entity.isNew()
          );
          const useLocalId = isEmpty(newEntity.id);

          return concat([
            saveEntityStore(this.entityType, newEntity, useLocalId),
            saveEntityNetwork(
              dispatchId,
              ActionsEnum.SAVE_EDITING_ENTITY,
              this.entityType,
              newEntity,
              useLocalId,
              originalEntity
            ),
          ]);
        })
      );
    })
  );

  /**
   * On save existing entity error, delete new entity and restore original entity in the store
   */
  protected saveExistingEntityError: Observable<
    DeleteEntityStore | SaveEntityStore
  > = this.actions.pipe(
    ofType(ActionsEnum.SAVE_ENTITY_NETWORK_ERROR),
    filter(
      ({ payload }: SaveEntityNetworkError) =>
        payload.entityType === this.entityType &&
        (payload.sourceAction === ActionsEnum.SAVE_EDITING_ENTITY ||
          payload.sourceAction === ActionsEnum.UPDATE_ENTITY)
    ),
    switchMap(({ payload }: SaveEntityNetworkError) => {
      const { entity, originalEntity } = payload;
      const actions: any[] = [
        deleteEntityStore(this.entityType, entity, false),
      ];

      if (originalEntity && !originalEntity.isNew()) {
        actions.push(saveEntityStore(this.entityType, originalEntity, false));
      }

      return concat(actions);
    })
  );

  /**
   * Save entity in the store:
   * - update existing entity in all lists
   * - OR add a new entity to target list
   */
  protected saveEntityStore: Observable<SetAllLists> = this.actions.pipe(
    ofType(ActionsEnum.SAVE_ENTITY_STORE),
    filter(
      ({ payload }: SaveEntityStore) => payload.entityType === this.entityType
    ),
    withLatestFrom(
      this.settingsDataStore.settings$,
      this.featuresDataStore.featureToggles$
    ),
    switchMap(
      ([{ payload }, settings, toggles]: [
        SaveEntityStore,
        Settings,
        Toggles
      ]) => {
        const { entity, useLocalId, listFilter } = payload;
        const idField = useLocalId
          ? this.model.KEYS.LOCAL_ID
          : this.model.KEYS.ID;
        const targetList =
          listFilter ||
          this.entityDataBuilder.buildListFilter(entity, settings, toggles);

        return this.entityDataStore.getAllLists().pipe(
          take(1),
          map((allLists: IFilteredLists) => {
            const newLists = reduce(
              allLists,
              (result, filteredList, listKey) => {
                // if not in the target list and list is empty, dont update the list
                if (listKey !== targetList && !filteredList.entities) {
                  return result;
                } else {
                  const currentEntities: List<any> =
                    filteredList.entities || List<any>([]);
                  const index: number = currentEntities.findIndex(
                    (e: any) => e[idField] === entity[idField]
                  );
                  const isNew: boolean = index === -1;

                  // if not in the target list and entity is new, dont update the list
                  if (isNew && listKey !== targetList) {
                    return result;
                  } else {
                    // if it is in the target list or it is an existing entity, update the list
                    const searchParams: ISearchParams =
                      filteredList.searchParams;
                    const comparatorFn = this.entityDataBuilder.buildSortItemsComparatorFn(
                      searchParams.sort_by as SortByEnum,
                      searchParams.sort_order as SortOrderEnum,
                      settings
                    );
                    const filterByFn = this.entityDataBuilder.buildListItemsFilterFn(
                      searchParams,
                      listKey,
                      settings,
                      toggles
                    );

                    let newEntities: List<any> = isNew
                      ? currentEntities.insert(0, entity)
                      : currentEntities.set(index, entity);
                    newEntities = newEntities.sort(comparatorFn) as List<any>;
                    newEntities = newEntities.filter(filterByFn) as List<any>;

                    const newList = {
                      ...filteredList,
                      entities: newEntities,
                    };

                    return {
                      ...result,
                      [listKey]: newList,
                    };
                  }
                }
              },
              allLists
            ) as IFilteredLists;

            return setAllLists(this.entityType, newLists);
          })
        );
      }
    )
  );

  /**
   * Delete entity from the store:
   * - delete existing entity from all lists
   */
  protected deleteEntityStore: Observable<SetAllLists> = this.actions.pipe(
    ofType(ActionsEnum.DELETE_ENTITY_STORE),
    filter(
      ({ payload }: DeleteEntityStore) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: DeleteEntityStore) => {
      const { entity, useLocalId } = payload;
      const idField = useLocalId
        ? this.model.KEYS.LOCAL_ID
        : this.model.KEYS.ID;

      return this.entityDataStore.getAllLists().pipe(
        take(1),
        map((allLists: IFilteredLists) => {
          const newLists = reduce(
            allLists,
            (result, filteredList, listKey) => {
              const currentEntities = filteredList.entities || List<any>([]);
              const newEntities = currentEntities.filterNot(
                (e: any) => e[idField] === entity[idField]
              ) as List<any>;

              if (currentEntities.size === newEntities.size) {
                return result;
              } else {
                const newList = {
                  ...filteredList,
                  entities: newEntities,
                };

                return {
                  ...result,
                  [listKey]: newList,
                };
              }
            },
            allLists
          ) as IFilteredLists;

          return setAllLists(this.entityType, newLists);
        })
      );
    })
  );

  /**
   * Save entity in the network
   */
  protected saveEntityToNetwork: Observable<
    | SaveEntityStore
    | SetEditingEntity
    | SaveEntityNetworkSuccess
    | SaveEntityNetworkError
    | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.SAVE_ENTITY_NETWORK),
    filter(
      ({ payload }: SaveEntityNetwork) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: SaveEntityNetwork) => {
      const {
        dispatchId,
        sourceAction,
        entity,
        useLocalId,
        originalEntity,
      } = payload;
      const action = ActionsEnum.SAVE_ENTITY_NETWORK;

      return this.entityDataService.saveEntity(entity).pipe(
        withLatestFrom(this.settingsDataStore.settings$),
        take(1),
        flatMap(([savedEntityProps, settings]) => {
          const newEntity = this.entityDataBuilder.buildEntity(
            savedEntityProps,
            settings,
            entity.isDirty(),
            entity.isNew()
          );
          const effects: any[] = [
            saveEntityStore(this.entityType, newEntity, useLocalId),
          ];

          if (sourceAction === ActionsEnum.SAVE_EDITING_ENTITY) {
            effects.push(setEditingEntity(this.entityType, newEntity));
          }

          effects.push(
            saveEntityNetworkSuccess(
              dispatchId,
              sourceAction,
              this.entityType,
              newEntity
            ),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              action,
              true,
              newEntity
            )
          );

          setTimeout(
            () => this.featuresDataDispatcher.fetchQuotaBannersCanvases(),
            UPDATE_FEATURE_FRAMEWORK_DATA_DELAY
          );

          return concat(effects);
        }),
        catchError((error) =>
          concat([
            saveEntityNetworkError(
              dispatchId,
              sourceAction,
              this.entityType,
              entity,
              originalEntity,
              error
            ),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              action,
              false,
              entity,
              new Error('save entity network error'),
              error
            ),
          ])
        )
      );
    })
  );

  /**
   * Delete entity in the network
   */
  protected deleteEntityNetwork: Observable<
    | DeleteEntityNetworkSuccess
    | SetDispatchedResponse
    | SaveEntityStore
    | DeleteEntityNetworkError
  > = this.actions.pipe(
    ofType(ActionsEnum.DELETE_ENTITY_NETWORK),
    filter(
      ({ payload }: DeleteEntityNetwork) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: DeleteEntityNetwork) => {
      const { dispatchId, entity } = payload;
      const action = ActionsEnum.DELETE_ENTITY_NETWORK;

      return this.entityDataService.deleteEntity(entity).pipe(
        take(1),
        flatMap(() => {
          setTimeout(
            () => this.featuresDataDispatcher.fetchQuotaBannersCanvases(),
            UPDATE_FEATURE_FRAMEWORK_DATA_DELAY
          );

          return concat([
            deleteEntityNetworkSuccess(
              dispatchId,
              action,
              this.entityType,
              entity
            ),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              action,
              true,
              entity
            ),
          ]);
        }),
        catchError((error) =>
          concat([
            saveEntityStore(this.entityType, entity, false),
            deleteEntityNetworkError(
              dispatchId,
              action,
              this.entityType,
              entity
            ),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              action,
              false,
              entity,
              new Error('delete entity network error'),
              error
            ),
          ])
        )
      );
    })
  );

  protected addToLimitedSelectedList: Observable<
    AddToSelectedList | SetGlobalBanner
  > = this.actions.pipe(
    ofType(ActionsEnum.ADD_TO_LIMITED_SELECTED_LIST),
    filter(
      ({ payload }: AddToLimitedSelectedList) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: AddToLimitedSelectedList) => {
      const { limit, entity } = payload;

      return this.entityDataStore.getSelectedEntities().pipe(
        take(1),
        map((selectedEntities: Set<U>) => {
          const blocked = selectedEntities && selectedEntities.size >= limit;

          if (blocked) {
            const bulkBannerId = `bulk_limit_warning_${this.entityType}`;

            return setGlobalBanner({
              type: GlobalBannerTypesEnum.info,
              id: bulkBannerId,
              message: this.translateService.instant(
                'WARNING.BULK_SELECT_LIMIT',
                {
                  maxNumber: limit,
                }
              ),
              containsHtml: false,
              dismissible: true,
            });
          } else {
            return addToSelectedList(this.entityType, entity);
          }
        })
      );
    })
  );

  protected hideBulkLimitBanner: Observable<HideGlobalBanner> = this.actions.pipe(
    ofType(
      ActionsEnum.REMOVE_FROM_SELECTED_LIST,
      ActionsEnum.RESET_SELECTED_LIST
    ),
    withLatestFrom(this.appStore.globalBanner$),
    filter(
      ([{ payload }, globalBanner]: [
        RemoveFromSelectedList | ResetSelectedList,
        IGlobalBanner
      ]) => {
        return payload.entityType === this.entityType && !isEmpty(globalBanner);
      }
    ),
    map(() => hideGlobalBanner(`bulk_limit_warning_${this.entityType}`))
  );

  /**
   * Clear list and refresh filters on tax year change
   */
  protected refreshListAndFiltersOnTaxYearChange: Observable<
    ClearList | SetFiltersFetched | FetchFilters
  > = this.actions.pipe(
    ofType(SettingsActionsEnum.EMIT_TAX_YEAR_CHANGED),
    flatMap(() => {
      const effects = [];
      const listFilters = keys(this.listFilterKeys);

      listFilters.forEach((listFilter: string) => {
        effects.push(clearList(this.entityType, listFilter));
        effects.push(setFiltersFetched(this.entityType, false, listFilter));
        effects.push(
          fetchFilters(this.entityType, this.refreshFiltersDelay, listFilter)
        );
      });

      return concat(effects);
    })
  );

  /**
   * Delete selected entities in the store
   */
  protected deleteSelectedEntities = this.actions.pipe(
    ofType(ActionsEnum.DELETE_SELECTED_ENTITIES),
    filter(
      ({ payload }: DeleteSelectedEntities) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: DeleteSelectedEntities) => {
      const { dispatchId, listFilter } = payload;

      return this.entityDataStore.getSelectedEntities().pipe(
        take(1),
        filter((x) => !!x),
        flatMap((selectedEntities: Set<U>) => {
          const sourceAction = ActionsEnum.DELETE_SELECTED_ENTITIES;
          const entityList = selectedEntities.toList();

          return concat([
            batchDeleteEntityStore(this.entityType, entityList),
            resetSelectedList(this.entityType),
            batchDeleteEntityNetwork(
              dispatchId,
              this.entityType,
              entityList,
              sourceAction,
              listFilter
            ),
          ]);
        })
      );
    })
  );

  /**
   * Batch delete entities from store
   */
  protected batchDeleteEntityStore: Observable<SetAllLists> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_DELETE_ENTITY_STORE),
    filter(
      ({ payload }: BatchDeleteEntityStore) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: BatchDeleteEntityStore) => {
      const { entities } = payload;
      const idField = this.model.KEYS.ID;

      return this.entityDataStore.getAllLists().pipe(
        take(1),
        map((allLists: IFilteredLists) => {
          const newLists = reduce(
            allLists,
            (result, filteredList, listKey) => {
              const currentEntities = filteredList.entities || List<any>([]);
              const newEntities = currentEntities.filterNot((currentEntity) =>
                entities.find(
                  (deleteEntity) =>
                    currentEntity[idField] === deleteEntity[idField]
                )
              ) as List<any>;

              if (currentEntities.size === newEntities.size) {
                return result;
              } else {
                const newList = {
                  ...filteredList,
                  entities: newEntities,
                };

                return {
                  ...result,
                  [listKey]: newList,
                };
              }
            },
            allLists
          ) as IFilteredLists;

          return setAllLists(this.entityType, newLists);
        })
      );
    })
  );

  /**
   * Delete a list of entities in the network
   */
  protected batchDeleteEntityNetwork = this.actions.pipe(
    ofType(ActionsEnum.BATCH_DELETE_ENTITY_NETWORK),
    filter(
      ({ payload }: BatchDeleteEntityNetwork) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: BatchDeleteEntityNetwork) => {
      const { dispatchId, entities, sourceAction, listFilter } = payload;
      const reqs = entities
        .map((entity: U) =>
          this.entityDataService.deleteEntity(entity).pipe(
            map(() => null),
            catchError(() => of(entity)) // collect entities that request failed
          )
        )
        .toArray();

      return forkJoin(reqs).pipe(
        flatMap((results: U[]) => {
          const failedEntities = compact(results);
          const effects = [];

          if (failedEntities.length) {
            const error = new Error(
              'partial batch delete entity network error'
            );
            const failedEntityList = List(failedEntities);

            effects.push(
              batchSaveEntityStore(
                this.entityType,
                failedEntityList,
                listFilter
              ),
              setSelectedList(this.entityType, failedEntityList.toSet()),
              batchDeleteEntityNetworkError(
                dispatchId,
                this.entityType,
                failedEntityList,
                sourceAction
              ),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                sourceAction,
                false,
                failedEntityList,
                error
              )
            );
          } else {
            effects.push(
              batchDeleteEntityNetworkSuccess(
                dispatchId,
                this.entityType,
                entities,
                sourceAction
              ),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                sourceAction,
                true,
                entities
              ),
              fetchFilters(
                this.entityType,
                this.refreshFiltersDelay,
                listFilter
              )
            );
          }

          setTimeout(
            () => this.featuresDataDispatcher.fetchQuotaBannersCanvases(),
            UPDATE_FEATURE_FRAMEWORK_DATA_DELAY
          );

          return concat(effects);
        }),
        catchError((errorData) => {
          const error = new Error('batch delete entity network error');

          return concat([
            batchDeleteEntityNetworkError(
              dispatchId,
              this.entityType,
              entities,
              sourceAction
            ),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              sourceAction,
              false,
              entities,
              error,
              errorData
            ),
          ]);
        })
      );
    })
  );

  /**
   * Save a list of entities in all lists in the store
   * @listFilter: all entities will be saved to this target list if this is present
   */
  protected batchSaveEntityStore: Observable<SetAllLists> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_SAVE_ENTITY_STORE),
    filter(
      ({ payload }: BatchSaveEntityStore) =>
        payload.entityType === this.entityType
    ),
    withLatestFrom(
      this.settingsDataStore.settings$,
      this.featuresDataStore.featureToggles$
    ),
    concatMap(
      ([{ payload }, settings, toggles]: [
        BatchSaveEntityStore,
        Settings,
        Toggles
      ]) => {
        const { entities, listFilter } = payload;
        const idField = this.model.KEYS.ID;

        // listFilter forces the entities to be saved as the target lists, used for search lists
        const initialTargetLists = listFilter ? { [listFilter]: entities } : {};

        // map entities by target lists. ex: { unpaid: [...], search: [...] } so that it's easier to save them in the
        // correct list in the steps below
        const targetLists = reduce(
          entities.toArray(),
          (result, entity) => {
            const listKey = this.entityDataBuilder.buildListFilter(
              entity,
              settings,
              toggles
            );
            const list = result[listKey] || List([]);

            result[listKey] = list.push(entity);

            return result;
          },
          initialTargetLists
        );

        return this.entityDataStore.getAllLists().pipe(
          take(1),
          map((allLists: IFilteredLists) => {
            const newLists = reduce(
              allLists,
              (result, filteredList, listKey) => {
                // get new entities from target list map
                const newEntities = targetLists[listKey] || List<any>([]);

                // filter existing entities that should replaced/removed form the current entities list
                const currentEntities = (filteredList.entities || List<any>([]))
                  .filterNot((currentEntity) =>
                    entities.find(
                      (newEntity) =>
                        currentEntity[idField] === newEntity[idField]
                    )
                  )
                  .toList();

                // concat existing and new entities
                let mergedLists = currentEntities.concat(
                  newEntities
                ) as List<any>;

                const searchParams: ISearchParams = filteredList.searchParams;
                const comparatorFn = this.entityDataBuilder.buildSortItemsComparatorFn(
                  searchParams.sort_by as SortByEnum,
                  searchParams.sort_order as SortOrderEnum,
                  settings
                );
                const filterByFn = this.entityDataBuilder.buildListItemsFilterFn(
                  searchParams,
                  listKey,
                  settings,
                  toggles
                );

                mergedLists = mergedLists
                  .sort(comparatorFn)
                  .filter(filterByFn) as List<any>;

                const newList = {
                  ...filteredList,
                  entities: mergedLists,
                };

                return {
                  ...result,
                  [listKey]: newList,
                };
              },
              allLists
            ) as IFilteredLists;

            return setAllLists(this.entityType, newLists);
          })
        );
      }
    )
  );

  /*********
   * HELPERS
   *********/
  buildUpdateList(
    entityType: EntityTypesEnum,
    entities: List<any>,
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings,
    toggles: Toggles,
    highlights?: List<Highlight>
  ): UpdateList {
    const comparatorFn = this.entityDataBuilder.buildSortItemsComparatorFn(
      searchParams.sort_by as SortByEnum,
      searchParams.sort_order as SortOrderEnum,
      settings
    );
    const filterByFn = this.entityDataBuilder.buildListItemsFilterFn(
      searchParams,
      listFilter,
      settings,
      toggles
    );

    return updateList(
      entityType,
      entities,
      searchParams,
      comparatorFn,
      filterByFn,
      highlights,
      listFilter
    );
  }

  // TODO [fiber]: clean up code related to filters delay, pending backend work on expenses
  /**
   * Refresh list filter with delay
   */
  protected fetchFiltersWithDelay(
    listFilter: string
  ): Observable<SetFilters | SetRefreshingFilters | FetchFiltersError> {
    return this.actions.pipe(
      ofType(ActionsEnum.FETCH_FILTERS),
      filter(
        ({ payload }: FetchFilters) =>
          payload.entityType === this.entityType &&
          payload.listFilter === listFilter &&
          payload.delay > 0
      ),
      flatMap(({ payload }: FetchFilters) => {
        const { listFilter: listFilterFromPayload, delay } = payload;

        // `of()` and `EMPTY` doesn't actually trigger the flatMap
        return of(void 0).pipe(
          observableDelay(delay),
          flatMap(() =>
            this.entityDataService.getFilters(listFilterFromPayload)
          ),
          take(1),
          flatMap((entityFilters: IEntityFilters) =>
            concat([
              setFilters(this.entityType, entityFilters, listFilterFromPayload),
              setRefreshingFilters(
                this.entityType,
                false,
                listFilterFromPayload
              ),
            ])
          ),
          catchError((error) =>
            concat([
              fetchFiltersError(
                this.entityType,
                new Error('fetch filters with delay error'),
                error,
                listFilterFromPayload
              ),
              setRefreshingFilters(
                this.entityType,
                false,
                listFilterFromPayload
              ),
            ])
          )
        );
      })
    );
  }
}
